/**
 *
 *  Registration
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 02/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useState, useEffect, useCallback } from "react";
import { useAppState } from "../../../state";
import { useSignUpState } from "../../../state/SignUpState";

// Components
import Button from "../../Button/Button";

// Icons
import ArrowRightIcon from "../../../icons/ArrowRightIcon";

export default function Registration({ ctaText, onNext }) {
  const { error } = useAppState();
  const { userFirstName, userFamilyName, setUserFirstName, setUserFamilyName } =
    useSignUpState();

  const [canSend, setCanSend] = useState(false);
  const [firstName, setFirstName] = useState(userFirstName);
  const [familyName, setFamilyName] = useState(userFamilyName);
  const [hasError, setHasError] = useState(false);

  const firstNameRef = React.useRef();
  const familyNameRef = React.useRef();

  useEffect(() => {
    if (isValidFirstName(userFirstName) && isValidLastName(userFamilyName)) {
      onNext();
    }

    if (firstNameRef.current) {
      firstNameRef.current.focus();
    }
  }, []);

  const handleFirstNameChange = useCallback((e) => {
    const value = e.target.value;

    setFirstName(value);

    setHasError(false);
  }, []);

  const handleFamilyNameChange = useCallback((e) => {
    const value = e.target.value;

    setFamilyName(value);
    setUserFamilyName(value);
    setHasError(false);
  }, []);

  useEffect(() => {
    if (isValidFirstName(firstName) && isValidLastName(familyName)) {
      setCanSend(true);
    } else {
      setCanSend(false);
    }
  }, [firstName, familyName]);

  const handleSubmit = (e) => {
    e.preventDefault();

    setUserFirstName(firstName);
    setUserFamilyName(familyName);

    onNext({ firstName, lastName: familyName });
  };

  return (
    <>
      <div className="tbk-text-title-bold tbk-mb-4 tbk-text-grey-main">
        What is Your Name?
      </div>
      <p className="tbk-text-main tbk-mb-4 tbk-hidden tbk-text-grey-main">
        Please, provide us your first name and last name. This information is
        required for future sessions bookings.
      </p>
      <form onSubmit={handleSubmit}>
        <div className="">
          <div className="tbk-text-small tbk-mb-0.5 tbk-text-black-grey">
            First:
          </div>
          <input
            ref={firstNameRef}
            type="text"
            className="tbk-input-field tbk-text-main tbk-mb-3 tbk-mr-1 tbk-pt-[11px] tbk-pl-2 tbk-pr-2 tbk-pb-[11px]"
            placeholder="First name"
            minLength={2}
            pattern="\s*(\S\s*){2,}"
            value={firstName}
            onInput={handleFirstNameChange}
          />
          <div className="tbk-text-small tbk-mb-0.5 tbk-text-black-grey">
            Last:
          </div>
          <input
            ref={familyNameRef}
            type="text"
            className="tbk-input-field tbk-text-main tbk-mb-3 tbk-mr-1 tbk-pt-[11px] tbk-pl-2 tbk-pr-2 tbk-pb-[11px]"
            placeholder="Last name"
            minLength={2}
            pattern="\s*(\S\s*){2,}"
            value={familyName}
            onInput={handleFamilyNameChange}
          />
        </div>
        {hasError ? (
          <div className="tbk-text-main tbk--mt-2 tbk-mb-2 tbk-text-center tbk-text-coral">
            {error ? error.message : "Unknown error has occurred"}
          </div>
        ) : null}
        <Button
          type="submit"
          className="tbk-mb-3 tbk-w-full"
          disabled={!canSend}
          endIcon={canSend ? null : <ArrowRightIcon />}
        >
          {ctaText || "Continue"}
        </Button>
      </form>
    </>
  );
}

function isValidFirstName(firstName) {
  // Check if firstName is empty or contains "user"
  return firstName && !firstName.includes("user") && firstName.length > 1;
}

function isValidLastName(lastName) {
  // Check if lastName is empty or does not contain only digits
  return lastName && !/^\d+$/.test(lastName) && lastName.length > 1;
}
